import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BackgroundRippleEffect } from "@/components/ui/background-ripple-effect";
import { Terminal, TypingAnimation } from "@/components/magicui/terminal";
import { HyperText } from "@/components/magicui/hyper-text";
import { useTranslation } from "react-i18next";
import TryNowSection from "../components/home/TryNowSection";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Clock, Shield, Zap, CheckCircle2 } from "lucide-react";
import type { RootState } from "@/store";
import { canAccessDashboard } from "@/utils/subscription-status";

const PublicHomePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  useEffect(() => {
    if (isAuthenticated) {
      navigate(canAccessDashboard(user) ? "/dashboard" : "/plans");
    }
  }, [isAuthenticated, user, navigate]);

  if (isLoading) {
    return (
      <div className="grid min-h-svh place-items-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* Hero Section - Full width on mobile/tablet */}
      <div className="relative z-10 w-full px-0 lg:px-3 xl:px-4 py-0  ">
        <div className="relative overflow-hidden rounded-none lg:rounded-2xl min-h-[600px] sm:min-h-[700px] flex items-center justify-center mx-0 lg:mx-auto lg:container">
          <BackgroundRippleEffect rows={14} cols={20} cellSize={48} />
          <div className="relative z-10 w-full text-center mb-8 sm:mb-12 md:mb-16 space-y-4 sm:space-y-6 px-4">
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-6xl xl:text-7xl font-bold text-center text-black dark:text-white font-sans tracking-tight leading-tight sm:leading-normal">
                {t("home.heroTitle")} <br />
                <HyperText
                  className="relative bg-clip-text text-transparent bg-[#db3100] [filter:drop-shadow(0px_1px_3px_rgba(27,_37,_80,_0.14))] text-5xl  lg:text-6xl xl:text-7xl font-bold tracking-tight"
                  duration={1200}
                  delay={800}
                  startOnView={true}
                  animateOnHover={true}
                  autoAnimateInterval={5000}
                  as="span"
                >
                  {t("home.heroTitleColored")}
                </HyperText>
              </h2>
              {/* Glow effect behind title - responsive */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-pink-500/20 to-purple-500/20 blur-2xl sm:blur-3xl -z-10 mx-8 sm:mx-20 md:mx-40" />
            </div>
            <p className="text-sm  sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-sm sm:max-w-2xl md:max-w-3xl mx-auto leading-relaxed px-2 sm:px-0">
              {t("home.heroSubtitle")}
            </p>
            <div className="flex justify-center pt-2 sm:pt-4">
              <Button
                size="lg"
                className="text-white border-0 shadow-xl hover:shadow-green-500/25 transition-all duration-300 px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold"
                onClick={() => {
                  navigate("/plans");
                }}
              >
                {t("home.tryForFree")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="relative z-10 container mx-auto px-3 sm:px-4 py-12 sm:py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white mb-4">
            {t("home.benefits.title")}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <Card className="glass-effect border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mb-4 group-hover:scale-110 transition-transform">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t("home.benefits.timeGain")}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t("home.benefits.timeGainDesc")}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full mb-4 group-hover:scale-110 transition-transform">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t("home.benefits.noErrors")}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t("home.benefits.noErrorsDesc")}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full mb-4 group-hover:scale-110 transition-transform">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t("home.benefits.easyToUse")}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t("home.benefits.easyToUseDesc")}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-effect border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full mb-4 group-hover:scale-110 transition-transform">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t("home.benefits.compliance")}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t("home.benefits.complianceDesc")}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Demo Section */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-8 sm:py-12 lg:py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white mb-4">
            {t("home.demo.title")}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t("home.demo.subtitle")}
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 max-w-6xl mx-auto items-center">
          {/* Input Form Preview */}
          <div className="flex-1 w-full lg:w-auto space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-slate-400 dark:text-slate-400 mb-4">
                {t("home.demo.form.title")}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("home.demo.form.fields.primeType")}
                  </span>
                  <span className="font-medium">
                    {t("home.demo.form.values.exceptionalPrime")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("home.demo.form.fields.percentage")}
                  </span>
                  <span className="font-medium">
                    {t("home.demo.form.values.fifteenPercent")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("home.demo.form.fields.calculationBase")}
                  </span>
                  <span className="font-medium">
                    {t("home.demo.form.values.baseSalary")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {t("home.demo.form.fields.condition")}
                  </span>
                  <span className="font-medium">
                    {t("home.demo.form.values.seniority")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="hidden lg:block">
            <div className="text-4xl text-gray-400">→</div>
          </div>

          {/* Terminal Demo */}
          <div className="flex-1 w-full lg:w-auto">
            <Terminal
              className="w-full max-w-none lg:w-[640px] h-[420px] sm:h-[420px] lg:h-[420px] bg-gray-800 dark:bg-gray-950 text-xs sm:text-sm lg:text-base overflow-hidden"
              sequence={false}
            >
              <TypingAnimation
                duration={30}
                delay={500}
                startOnView={true}
                className="text-green-400"
              >
                {t("home.demo.terminal.command")}
              </TypingAnimation>

              <TypingAnimation
                duration={30}
                delay={3000}
                startOnView={true}
                className="text-blue-400"
              >
                {t("home.demo.terminal.validating")}
              </TypingAnimation>

              <TypingAnimation
                duration={30}
                delay={4000}
                startOnView={true}
                className="text-yellow-400"
              >
                {t("home.demo.terminal.generating")}
              </TypingAnimation>

              <TypingAnimation
                duration={30}
                delay={5000}
                startOnView={true}
                className="text-gray-300"
              >
                {`Begin
  ${t("home.demo.terminal.code.comment")}
  If emp.anciennete >= 6 Then
    Exec ("Lprime_D02")
    methodeCalcul = 1
    Bases = bul.salairedebase 
    Tauxs = 15
    Liblong = "${t("home.demo.terminal.code.liblong")}"
    Exec ("GenereLprime")
  Endif
End`}
              </TypingAnimation>

              <TypingAnimation
                duration={20}
                delay={12500}
                startOnView={true}
                className="text-green-400"
              >
                {t("home.demo.terminal.success")}
              </TypingAnimation>

              <TypingAnimation
                duration={30}
                delay={13000}
                startOnView={true}
                className="text-cyan-400"
              >
                {t("home.demo.terminal.ready")}
              </TypingAnimation>
            </Terminal>
          </div>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <TryNowSection />
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes cell-ripple {
          0% {
            background-color: var(--cell-fill-color);
            opacity: 0.4;
            transform: scale(1);
          }
          15% {
            background-color: rgba(59, 130, 246, 0.8);
            opacity: 1;
            transform: scale(1.1);
          }
          100% {
            background-color: var(--cell-fill-color);
            opacity: 0.4;
            transform: scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animate-cell-ripple {
          animation: cell-ripple var(--duration, 400ms) ease-out;
          animation-delay: var(--delay, 0ms);
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .perspective-1000 { perspective: 1000px; }
        .transform-style-preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}</style>
    </div>
  );
};

export default PublicHomePage;
